import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject, type SetStateAction } from "react";
import { registerUpdateBlocker } from "./update-activity";

export function readComposerDraft(storage: Pick<Storage, "getItem">, key: string | null): string {
  try { return key === null ? "" : storage.getItem(key) ?? ""; } catch { return ""; }
}

export function saveComposerDraft(storage: Pick<Storage, "setItem" | "removeItem">, key: string | null, body: string): boolean {
  try {
    if (key !== null) {
      if (body) storage.setItem(key, body);
      else storage.removeItem(key);
    }
    return true;
  } catch { return body.length === 0; }
}

/** Save on input, not an effect that can overwrite another chat's draft. */
export function useComposerDraft(key: string | null, editing: boolean): [string, (value: SetStateAction<string>) => void] {
  const [, render] = useState(0);
  const draft = useRef<{ key: string | null; body: string; saved: boolean } | null>(null);
  const editingRef = useRef(editing);
  editingRef.current = editing;
  if (draft.current === null || draft.current.key !== key) {
    let body = "";
    try { body = readComposerDraft(window.sessionStorage, key); } catch { /* Storage can be disabled. */ }
    draft.current = { key, body, saved: true };
  }
  useEffect(() => registerUpdateBlocker(() => editingRef.current || draft.current?.saved === false), []);
  const setDraft = useCallback((value: SetStateAction<string>) => {
    const body = typeof value === "function" ? value(draft.current?.body ?? "") : value;
    let saved = body.length === 0;
    if (!editingRef.current) {
      try { saved = saveComposerDraft(window.sessionStorage, key, body); } catch { saved = body.length === 0; }
    }
    draft.current = { key, body, saved };
    render(count => count + 1);
  }, [key]);
  return [draft.current.body, setDraft];
}

export function insertAtSelection(body: string, text: string, start: number, end: number) {
  const from = Math.max(0, Math.min(start, body.length));
  const to = Math.max(from, Math.min(end, body.length));
  return { body: body.slice(0, from) + text + body.slice(to), caret: from + text.length };
}

/** Textarea selection survives blur while a picker is open. Never send on pick. */
export function useComposerInsert(ref: RefObject<HTMLTextAreaElement | null>, body: string, setBody: (body: string) => void) {
  const pending = useRef<number | null>(null);
  useLayoutEffect(() => {
    if (pending.current === null || !ref.current) return;
    ref.current.focus({ preventScroll: true });
    ref.current.setSelectionRange(pending.current, pending.current);
    pending.current = null;
  });
  return (text: string) => {
    if (ref.current?.disabled) return;
    const next = insertAtSelection(body, text, ref.current?.selectionStart ?? body.length, ref.current?.selectionEnd ?? body.length);
    if (next.body === body && ref.current) {
      ref.current.focus({ preventScroll: true });
      ref.current.setSelectionRange(next.caret, next.caret);
      return;
    }
    pending.current = next.caret;
    setBody(next.body);
  };
}

export function useComposerAutoGrow(ref: RefObject<HTMLTextAreaElement | null>, value: string) {
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const resize = () => {
      element.style.height = "0px";
      element.style.height = `${Math.max(36, Math.min(element.scrollHeight, 160))}px`;
      element.style.overflowY = element.scrollHeight > 160 ? "auto" : "hidden";
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [ref, value]);
}
